import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { performLogout } from '../../utils/logout';
import { getSafeRedirect } from '../../utils/safeRedirect';
import AuthLayout from './AuthLayout';
import AuthStatus from './AuthStatus';

/** How long "You're signed out" stays up before the sign-in page. */
export const SIGNED_OUT_REDIRECT_MS = 1500;

const Logout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Tracks pending navigation timers so they can be cancelled on unmount.
  // Without this a timer could call navigate() after the component was gone,
  // and StrictMode's double-mount scheduled two of them.
  const pendingTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  useEffect(() => () => {
    pendingTimersRef.current.forEach(clearTimeout);
    pendingTimersRef.current = [];
  }, []);
  const [status, setStatus] = useState<'signing-out' | 'success' | 'error'>('signing-out');

  // `/logout?redirect=/x` signs out, then asks the next person to sign in
  // and return to /x (e.g. an invitation opened while signed in to the wrong
  // account). Only same-origin app paths are honoured.
  const returnTo = getSafeRedirect(location.search);
  const signInPath = returnTo ? `/login?redirect=${encodeURIComponent(returnTo)}` : '/login';

  const signOut = useCallback(async () => {
    setStatus('signing-out');
    try {
      // Signs out of Firebase in every open tab and drops every cached
      // GraphQL result, so the next user to sign in on this browser can't
      // briefly see the previous user's projects and billing.
      await performLogout();

      setStatus('success');

      // Redirect after a brief confirmation. Replace, so Back doesn't
      // return to this page (which would sign out again).
      pendingTimersRef.current.push(setTimeout(() => {
        navigate(signInPath, { replace: true });
      }, SIGNED_OUT_REDIRECT_MS));
    } catch (error: unknown) {
      console.error('Error signing out:', error);
      setStatus('error');
    }
  }, [navigate, signInPath]);

  useEffect(() => {
    signOut();
  }, [signOut]);

  if (status === 'success') {
    return (
      <AuthLayout>
        <AuthStatus
          tone="success"
          icon={<LogoutOutlinedIcon />}
          title="You're signed out"
          description="Taking you to the sign-in page…"
          primaryAction={{
            label: 'Sign in',
            onClick: () => navigate(signInPath, { replace: true }),
          }}
        />
      </AuthLayout>
    );
  }

  if (status === 'error') {
    return (
      <AuthLayout>
        <AuthStatus
          tone="error"
          title="We couldn't sign you out"
          description="Check your connection and try again. You're still signed in on this device."
          primaryAction={{ label: 'Try again', onClick: signOut }}
          secondaryAction={{ label: 'Back to dashboard', to: '/dashboard' }}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Box role="status">
        <AuthStatus
          tone="neutral"
          icon={<CircularProgress size={22} color="inherit" />}
          title="Signing you out"
          description="This only takes a moment."
        />
      </Box>
    </AuthLayout>
  );
};

export default Logout;
